export type StripeTaxIdType = {
  countryCode: string;
  country: string;
  code: string;
  name: string;
  example: string;
  uniqueCode: string;
};

type StripeTaxIdTypeMap = Record<string, StripeTaxIdType[]>;

type StripeTaxIdCodeMap = Record<string, StripeTaxIdType>;

/**
 * A list of supported tax ID types for Stripe.
 * Note: The uniqueCode is a combination of the country code and the tax code.
 * This combination may not be unique across all countries. Ensure that the uniqueCode is unique
 * across all entries when adding new countries to the list.
 */
const STRIPE_TAX_ID_TYPES: StripeTaxIdType[] = [
  {
    countryCode: 'AD',
    country: 'Andorra',
    code: 'ad_nrt',
    name: 'Andorran NRT number',
    example: 'A-123456-Z',
    uniqueCode: 'AD-ad_nrt',
  },
  {
    countryCode: 'AR',
    country: 'Argentina',
    code: 'ar_cuit',
    name: 'Argentinian tax ID number',
    example: '12-3456789-01',
    uniqueCode: 'AR-ar_cuit',
  },
  {
    countryCode: 'AT',
    country: 'Austria',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'ATU12345678',
    uniqueCode: 'AT-eu_vat',
  },
  {
    countryCode: 'AU',
    country: 'Australia',
    code: 'au_arn',
    name: 'Australian Taxation Office Reference Number',
    example: '123456789123',
    uniqueCode: 'AU-au_arn',
  },
  {
    countryCode: 'AU',
    country: 'Australia',
    code: 'au_abn',
    name: 'Australian Business Number (AU ABN)',
    example: '12345678912',
    uniqueCode: 'AU-au_abn',
  },
  {
    countryCode: 'BE',
    country: 'Belgium',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'BE0123456789',
    uniqueCode: 'BE-eu_vat',
  },
  {
    countryCode: 'BH',
    country: 'Bahrain',
    code: 'bh_vat',
    name: 'Bahraini VAT Number',
    example: '123456789012345',
    uniqueCode: 'BH-bh_vat',
  },
  {
    countryCode: 'BY',
    country: 'Belarus',
    code: 'by_tin',
    name: 'Belarus TIN Number',
    example: '123456789',
    uniqueCode: 'BY-by_tin',
  },
  {
    countryCode: 'BO',
    country: 'Bolivia',
    code: 'bo_tin',
    name: 'Bolivian tax ID',
    example: '123456789',
    uniqueCode: 'BO-bo_tin',
  },
  {
    countryCode: 'BG',
    country: 'Bulgaria',
    code: 'bg_uic',
    name: 'Bulgaria Unified Identification Code',
    example: '123456789',
    uniqueCode: 'BG-bg_uic',
  },
  {
    countryCode: 'BG',
    country: 'Bulgaria',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'BG0123456789',
    uniqueCode: 'BG-eu_vat',
  },
  {
    countryCode: 'BR',
    country: 'Brazil',
    code: 'br_cpf',
    name: 'Brazilian CPF number',
    example: '123.456.789-87',
    uniqueCode: 'BR-br_cpf',
  },
  {
    countryCode: 'BR',
    country: 'Brazil',
    code: 'br_cnpj',
    name: 'Brazilian CNPJ number',
    example: '01.234.456/5432-10',
    uniqueCode: 'BR-br_cnpj',
  },
  {
    countryCode: 'CA',
    country: 'Canada',
    code: 'ca_qst',
    name: 'Canadian QST number (Québec)',
    example: '1234567890TQ1234',
    uniqueCode: 'CA-ca_qst',
  },
  {
    countryCode: 'CA',
    country: 'Canada',
    code: 'ca_bn',
    name: 'Canadian BN',
    example: '123456789',
    uniqueCode: 'CA-ca_bn',
  },
  {
    countryCode: 'CA',
    country: 'Canada',
    code: 'ca_pst_sk',
    name: 'Canadian PST number (Saskatchewan)',
    example: '1234567',
    uniqueCode: 'CA-ca_pst_sk',
  },
  {
    countryCode: 'CA',
    country: 'Canada',
    code: 'ca_pst_mb',
    name: 'Canadian PST number (Manitoba)',
    example: '123456-7',
    uniqueCode: 'CA-ca_pst_mb',
  },
  {
    countryCode: 'CA',
    country: 'Canada',
    code: 'ca_pst_bc',
    name: 'Canadian PST number (British Columbia)',
    example: 'PST-1234-5678',
    uniqueCode: 'CA-ca_pst_bc',
  },
  {
    countryCode: 'CA',
    country: 'Canada',
    code: 'ca_gst_hst',
    name: 'Canadian GST/HST number',
    example: '123456789RT0002',
    uniqueCode: 'CA-ca_gst_hst',
  },
  {
    countryCode: 'CL',
    country: 'Chile',
    code: 'cl_tin',
    name: 'Chilean TIN',
    example: '12.345.678-K',
    uniqueCode: 'CL-cl_tin',
  },
  {
    countryCode: 'CN',
    country: 'China',
    code: 'cn_tin',
    name: 'Chinese tax ID',
    example: '123456789012345678',
    uniqueCode: 'CN-cn_tin',
  },
  {
    countryCode: 'CO',
    country: 'Colombia',
    code: 'co_nit',
    name: 'Colombian NIT number',
    example: '123.456.789-0',
    uniqueCode: 'CO-co_nit',
  },
  {
    countryCode: 'CR',
    country: 'Costa Rica',
    code: 'cr_tin',
    name: 'Costa Rican tax ID',
    example: '1-234-567890',
    uniqueCode: 'CR-cr_tin',
  },
  {
    countryCode: 'CY',
    country: 'Cyprus',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'CY12345678Z',
    uniqueCode: 'CY-eu_vat',
  },
  {
    countryCode: 'CZ',
    country: 'Czech Republic',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'CZ1234567890',
    uniqueCode: 'CZ-eu_vat',
  },
  {
    countryCode: 'DK',
    country: 'Denmark',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'DK12345678',
    uniqueCode: 'DK-eu_vat',
  },
  {
    countryCode: 'HR',
    country: 'Croatia',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'HR12345678912',
    uniqueCode: 'HR-eu_vat',
  },
  {
    countryCode: 'HR',
    country: 'Croatia',
    code: 'hr_oib',
    name: 'Croatian Personal Identification Number',
    example: '12345678901',
    uniqueCode: 'HR-hr_oib',
  },
  {
    countryCode: 'DO',
    country: 'Dominican Republic',
    code: 'do_rcn',
    name: 'Dominican RCN number',
    example: '123-4567890-1',
    uniqueCode: 'DO-do_rcn',
  },
  {
    countryCode: 'EC',
    country: 'Ecuador',
    code: 'ec_ruc',
    name: 'Ecuadorian RUC number',
    example: '1234567890001',
    uniqueCode: 'EC-ec_ruc',
  },
  {
    countryCode: 'EG',
    country: 'Egypt',
    code: 'eg_tin',
    name: 'Egyptian Tax Identification Number',
    example: '123456789',
    uniqueCode: 'EG-eg_tin',
  },
  {
    countryCode: 'HK',
    country: 'Hong Kong',
    code: 'hk_br',
    name: 'Hong Kong BR number',
    example: '12345678',
    uniqueCode: 'HK-hk_br',
  },
  {
    countryCode: 'HU',
    country: 'Hungary',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'HU12345678912',
    uniqueCode: 'HU-eu_vat',
  },
  {
    countryCode: 'IN',
    country: 'India',
    code: 'in_gst',
    name: 'Indian GST number',
    example: '12ABCDE3456FGZH',
    uniqueCode: 'IN-in_gst',
  },
  {
    countryCode: 'ID',
    country: 'Indonesia',
    code: 'id_npwp',
    name: 'Indonesian NPWP number',
    example: '12.345.678.9-012.345',
    uniqueCode: 'ID-id_npwp',
  },
  {
    countryCode: 'IE',
    country: 'Ireland',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'IE1234567AB',
    uniqueCode: 'IE-eu_vat',
  },
  {
    countryCode: 'IT',
    country: 'Italy',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'IT12345678912',
    uniqueCode: 'IT-eu_vat',
  },
  {
    countryCode: 'LT',
    country: 'Lithuania',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'LT123456789123',
    uniqueCode: 'LT-eu_vat',
  },
  {
    countryCode: 'LV',
    country: 'Latvia',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'LV12345678912',
    uniqueCode: 'LV-eu_vat',
  },
  {
    countryCode: 'MT',
    country: 'Malta',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'MT12345678',
    uniqueCode: 'MT-eu_vat',
  },
  {
    countryCode: 'NL',
    country: 'Netherlands',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'NL123456789B12',
    uniqueCode: 'NL-eu_vat',
  },
  {
    countryCode: 'PT',
    country: 'Portugal',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'PT123456789',
    uniqueCode: 'PT-eu_vat',
  },
  {
    countryCode: 'RO',
    country: 'Romania',
    code: 'ro_tin',
    name: 'Romanian tax ID number',
    example: '1234567890123',
    uniqueCode: 'RO-ro_tin',
  },
  {
    countryCode: 'SK',
    country: 'Slovakia',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'SK1234567891',
    uniqueCode: 'SK-eu_vat',
  },
  {
    countryCode: 'SI',
    country: 'Slovenia',
    code: 'si_tin',
    name: 'Slovenia tax number (davčna številka)',
    example: '12345678',
    uniqueCode: 'SI-si_tin',
  },
  {
    countryCode: 'SI',
    country: 'Slovenia',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'SI12345678',
    uniqueCode: 'SI-eu_vat',
  },
  {
    countryCode: 'GB',
    country: 'United Kingdom',
    code: 'gb_vat',
    name: 'United Kingdom VAT number',
    example: 'GB123456789',
    uniqueCode: 'GB-gb_vat',
  },
  {
    countryCode: 'ES',
    country: 'Spain',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'ESA1234567Z',
    uniqueCode: 'ES-eu_vat',
  },
  {
    countryCode: 'EE',
    country: 'Estonia',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'EE123456789',
    uniqueCode: 'EE-eu_vat',
  },
  {
    countryCode: 'EU',
    country: 'EU',
    code: 'eu_oss_vat',
    name: 'European One Stop Shop VAT number for non-Union scheme',
    example: 'EU123456789',
    uniqueCode: 'EU-eu_oss_vat',
  },
  {
    countryCode: 'FI',
    country: 'Finland',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'FI12345678',
    uniqueCode: 'FI-eu_vat',
  },
  {
    countryCode: 'FR',
    country: 'France',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'FRAB123456789',
    uniqueCode: 'FR-eu_vat',
  },
  {
    countryCode: 'GE',
    country: 'Georgia',
    code: 'ge_vat',
    name: 'Georgian VAT',
    example: '123456789',
    uniqueCode: 'GE-ge_vat',
  },
  {
    countryCode: 'RO',
    country: 'Romania',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'RO1234567891',
    uniqueCode: 'RO-eu_vat',
  },
  {
    countryCode: 'GB',
    country: 'United Kingdom',
    code: 'eu_vat',
    name: 'Northern Ireland VAT number',
    example: 'XI123456789',
    uniqueCode: 'GB-eu_vat',
  },
  {
    countryCode: 'DE',
    country: 'Germany',
    code: 'de_stn',
    name: 'German Tax Number (Steuernummer)',
    example: '1234567890',
    uniqueCode: 'DE-de_stn',
  },
  {
    countryCode: 'DE',
    country: 'Germany',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'DE123456789',
    uniqueCode: 'DE-eu_vat',
  },
  {
    countryCode: 'GR',
    country: 'Greece',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'EL123456789',
    uniqueCode: 'GR-eu_vat',
  },
  {
    countryCode: 'HU',
    country: 'Hungary',
    code: 'hu_tin',
    name: 'Hungary tax number (adószám)',
    example: '12345678-1-23',
    uniqueCode: 'HU-hu_tin',
  },
  {
    countryCode: 'IS',
    country: 'Iceland',
    code: 'is_vat',
    name: 'Icelandic VAT',
    example: '123456',
    uniqueCode: 'IS-is_vat',
  },
  {
    countryCode: 'IL',
    country: 'Israel',
    code: 'il_vat',
    name: 'Israel VAT',
    example: '000012345',
    uniqueCode: 'IL-il_vat',
  },
  {
    countryCode: 'JP',
    country: 'Japan',
    code: 'jp_cn',
    name: 'Japanese Corporate Number (Hōjin Bangō)',
    example: '1234567891234',
    uniqueCode: 'JP-jp_cn',
  },
  {
    countryCode: 'JP',
    country: 'Japan',
    code: 'jp_rn',
    name: "Japanese Registered Foreign Businesses' Registration Number (*Tōroku Kokugai Jigyōsha no Tōroku Bangō*)",
    example: '12345',
    uniqueCode: 'JP-jp_rn',
  },
  {
    countryCode: 'JP',
    country: 'Japan',
    code: 'jp_trn',
    name: 'Japanese Tax Registration Number (*Tōroku Bangō*)',
    example: 'T1234567891234',
    uniqueCode: 'JP-jp_trn',
  },
  {
    countryCode: 'KR',
    country: 'Korea, Republic of',
    code: 'kr_brn',
    name: 'Korean BRN',
    example: '123-45-67890',
    uniqueCode: 'KR-kr_brn',
  },
  {
    countryCode: 'KZ',
    country: 'Kazakhstan',
    code: 'kz_bin',
    name: 'Kazakhstani Business Identification Number',
    example: '123456789012',
    uniqueCode: 'KZ-kz_bin',
  },
  {
    countryCode: 'KE',
    country: 'Kenya',
    code: 'ke_pin',
    name: 'Kenya Revenue Authority Personal Identification Number',
    example: 'P000111111A',
    uniqueCode: 'KE-ke_pin',
  },
  {
    countryCode: 'KR',
    country: 'South Korea',
    code: 'kr_brn',
    name: 'Korean BRN',
    example: '123-45-67890',
    uniqueCode: 'KR-kr_brn-2',
  },
  {
    countryCode: 'LI',
    country: 'Liechtenstein',
    code: 'li_uid',
    name: 'Liechtensteinian UID number',
    example: 'CHE123456789',
    uniqueCode: 'LI-li_uid',
  },
  {
    countryCode: 'LU',
    country: 'Luxembourg',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'LU12345678',
    uniqueCode: 'LU-eu_vat',
  },
  {
    countryCode: 'MA',
    country: 'Morocco',
    code: 'ma_vat',
    name: 'Morocco VAT Number',
    example: '12345678',
    uniqueCode: 'MA-ma_vat',
  },
  {
    countryCode: 'MX',
    country: 'Mexico',
    code: 'mx_rfc',
    name: 'Mexican RFC number',
    example: 'ABC010203AB9',
    uniqueCode: 'MX-mx_rfc',
  },
  {
    countryCode: 'MY',
    country: 'Malaysia',
    code: 'my_frp',
    name: 'Malaysian FRP number',
    example: '12345678',
    uniqueCode: 'MY-my_frp',
  },
  {
    countryCode: 'MY',
    country: 'Malaysia',
    code: 'my_itn',
    name: 'Malaysian ITN',
    example: 'C 1234567890',
    uniqueCode: 'MY-my_itn',
  },
  {
    countryCode: 'MY',
    country: 'Malaysia',
    code: 'my_sst',
    name: 'Malaysian SST number',
    example: 'A12-3456-78912345',
    uniqueCode: 'MY-my_sst',
  },
  {
    countryCode: 'MD',
    country: 'Moldova',
    code: 'md_vat',
    name: 'Moldova VAT Number',
    example: '1234567',
    uniqueCode: 'MD-md_vat',
  },
  {
    countryCode: 'NG',
    country: 'Nigeria',
    code: 'ng_tin',
    name: 'Nigerian Tax Identification Number',
    example: '12345678-0001',
    uniqueCode: 'NG-ng_tin',
  },
  {
    countryCode: 'NO',
    country: 'Norway',
    code: 'no_voec',
    name: 'Norwegian VAT on e-commerce number',
    example: '1234567',
    uniqueCode: 'NO-no_voec',
  },
  {
    countryCode: 'NO',
    country: 'Norway',
    code: 'no_vat',
    name: 'Norwegian VAT number',
    example: '123456789MVA',
    uniqueCode: 'NO-no_vat',
  },
  {
    countryCode: 'NZ',
    country: 'New Zealand',
    code: 'nz_gst',
    name: 'New Zealand GST number',
    example: '123456789',
    uniqueCode: 'NZ-nz_gst',
  },
  {
    countryCode: 'OM',
    country: 'Oman',
    code: 'om_vat',
    name: 'Omani VAT Number',
    example: 'OM1234567890',
    uniqueCode: 'OM-om_vat',
  },
  {
    countryCode: 'PE',
    country: 'Peru',
    code: 'pe_ruc',
    name: 'Peruvian RUC number',
    example: '12345678901',
    uniqueCode: 'PE-pe_ruc',
  },
  {
    countryCode: 'RS',
    country: 'Serbia',
    code: 'rs_pib',
    name: 'Serbian PIB number',
    example: '123456789',
    uniqueCode: 'RS-rs_pib',
  },
  {
    countryCode: 'RU',
    country: 'Russia',
    code: 'ru_kpp',
    name: 'Russian KPP',
    example: '123456789',
    uniqueCode: 'RU-ru_kpp',
  },
  {
    countryCode: 'RU',
    country: 'Russian Federation',
    code: 'ru_inn',
    name: 'Russian INN',
    example: '1234567891',
    uniqueCode: 'RU-ru_inn',
  },
  {
    countryCode: 'SA',
    country: 'Saudi Arabia',
    code: 'sa_vat',
    name: 'Saudi Arabia VAT',
    example: '123456789012345',
    uniqueCode: 'SA-sa_vat',
  },
  {
    countryCode: 'SG',
    country: 'Singapore',
    code: 'sg_uen',
    name: 'Singaporean UEN',
    example: '123456789F',
    uniqueCode: 'SG-sg_uen',
  },
  {
    countryCode: 'SG',
    country: 'Singapore',
    code: 'sg_gst',
    name: 'Singaporean GST',
    example: 'M12345678X',
    uniqueCode: 'SG-sg_gst',
  },
  {
    countryCode: 'SV',
    country: 'El Salvador',
    code: 'sv_nit',
    name: 'El Salvadorian NIT number',
    example: '1234-567890-123-4',
    uniqueCode: 'SV-sv_nit',
  },
  {
    countryCode: 'PH',
    country: 'Philippines',
    code: 'ph_tin',
    name: 'Philippines Tax Identification Number',
    example: '123456789012',
    uniqueCode: 'PH-ph_tin',
  },
  {
    countryCode: 'PL',
    country: 'Poland',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'PL1234567890',
    uniqueCode: 'PL-eu_vat',
  },
  {
    countryCode: 'SE',
    country: 'Sweden',
    code: 'eu_vat',
    name: 'European VAT number',
    example: 'SE123456789123',
    uniqueCode: 'SE-eu_vat',
  },
  {
    countryCode: 'ES',
    country: 'Spain',
    code: 'es_cif',
    name: 'Spanish CIF number',
    example: 'A12345678',
    uniqueCode: 'ES-es_cif',
  },
  {
    countryCode: 'CH',
    country: 'Switzerland',
    code: 'ch_uid',
    name: 'Switzerland UID number',
    example: 'CHE-123.456.789 HR',
    uniqueCode: 'CH-ch_uid',
  },
  {
    countryCode: 'CH',
    country: 'Switzerland',
    code: 'ch_vat',
    name: 'Switzerland VAT number',
    example: 'CHE-123.456.789 MWST',
    uniqueCode: 'CH-ch_vat',
  },
  {
    countryCode: 'TH',
    country: 'Thailand',
    code: 'th_vat',
    name: 'Thai VAT',
    example: '1234567891234',
    uniqueCode: 'TH-th_vat',
  },
  {
    countryCode: 'TW',
    country: 'Taiwan',
    code: 'tw_vat',
    name: 'Taiwanese VAT',
    example: '12345678',
    uniqueCode: 'TW-tw_vat',
  },
  {
    countryCode: 'TZ',
    country: 'Tanzania',
    code: 'tz_vat',
    name: 'Tanzania VAT Number',
    example: '12345678A',
    uniqueCode: 'TZ-tz_vat',
  },
  {
    countryCode: 'UA',
    country: 'Ukraine',
    code: 'ua_vat',
    name: 'Ukrainian VAT',
    example: '123456789',
    uniqueCode: 'UA-ua_vat',
  },
  {
    countryCode: 'AE',
    country: 'United Arab Emirates',
    code: 'ae_trn',
    name: 'United Arab Emirates TRN',
    example: '123456789012345',
    uniqueCode: 'AE-ae_trn',
  },
  {
    countryCode: 'US',
    country: 'United States',
    code: 'us_ein',
    name: 'United States EIN',
    example: '12-3456789',
    uniqueCode: 'US-us_ein',
  },
  {
    countryCode: 'ZA',
    country: 'South Africa',
    code: 'za_vat',
    name: 'South African VAT number',
    example: '4123456789',
    uniqueCode: 'ZA-za_vat',
  },
  {
    countryCode: 'TR',
    country: 'Turkey',
    code: 'tr_tin',
    name: 'Turkish Tax Identification Number',
    example: '0123456789',
    uniqueCode: 'TR-tr_tin',
  },
  {
    countryCode: 'UY',
    country: 'Uruguay',
    code: 'uy_ruc',
    name: 'Uruguayan RUC number',
    example: '123456789012',
    uniqueCode: 'UY-uy_ruc',
  },
  {
    countryCode: 'UZ',
    country: 'Uzbekistan',
    code: 'uz_vat',
    name: 'Uzbekistan VAT Number',
    example: '123456789012',
    uniqueCode: 'UZ-uz_vat',
  },
  {
    countryCode: 'UZ',
    country: 'Uzbekistan',
    code: 'uz_tin',
    name: 'Uzbekistan TIN Number',
    example: '123456789',
    uniqueCode: 'UZ-uz_tin',
  },
  {
    countryCode: 'VE',
    country: 'Venezuela',
    code: 've_rif',
    name: 'Venezuelan RIF number',
    example: 'A-12345678-9',
    uniqueCode: 'VE-ve_rif',
  },
  {
    countryCode: 'VN',
    country: 'Vietnam',
    code: 'vn_tin',
    name: 'Vietnamese tax ID number',
    example: '1234567890',
    uniqueCode: 'VN-vn_tin',
  },
];

export const stripeTaxIdTypeMap = STRIPE_TAX_ID_TYPES.reduce<StripeTaxIdTypeMap>(
  (map: StripeTaxIdTypeMap, taxIdType: StripeTaxIdType) => ({
    ...map,
    // @ts-expect-error TS2488 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    [taxIdType.countryCode]: map[taxIdType.countryCode] ? [...map[taxIdType.countryCode], taxIdType] : [taxIdType],
  }),
  {}
);

export const stripeTaxIdCodeMap = STRIPE_TAX_ID_TYPES.reduce<StripeTaxIdCodeMap>(
  (map: StripeTaxIdCodeMap, taxIdType: StripeTaxIdType) => ({
    ...map,
    [taxIdType.code]: taxIdType,
  }),
  {}
);

export default STRIPE_TAX_ID_TYPES;
